import { broker } from '#~/agent/broker.js';
import { Bridge } from '#~/agent/components/bridge/bridge.js';
import { Linker } from '#~/agent/components/linker/index.js';
import { LocalModeSwitch } from '#~/agent/components/local-mode/index.js';
import * as Monitor from '#~/agent/components/monitoring/index.js';
import { useAgents } from '#~/agent/hooks.js';
import type { Proxy } from '#~/agent/types.js';
import { SessionsAvatars } from '#~/room/components/sessions-avatars/sessions.js';
import { InviteButton } from '#~/room/components/user-invite/button.js';
import { pick } from 'lodash-es';
import {
  fieldKeyedAgentsEqual,
  placeholderAgentPick
} from '#~/agent/provider/use-agents-field-eq.js';
import type { ReactElement } from 'react';
import { Production } from '../../room/components/production/index.js';
import * as Scaffold from '../scaffold/index.js';
import type { PlaceholderProps } from './types.js';
import {
  SingleBridgeOffline,
  AllBridgesOffline
} from '#~/agent/components/bridge/screens/offline.js';

/** `<Placeholder>` component. */
export const Placeholder = ({ roomId, ...props }: PlaceholderProps): ReactElement => {
  // While the below generally works, agents that are offline are not being displayed.
  // Their version and name have an empty string (""") value.
  //
  // pick() returns a new object reference on every call; zustand/shallow would
  // always return false, causing Placeholder to re-render on every unrelated
  // store mutation (e.g. embedded-agent changes). Compare selected fields
  // directly so only genuine changes to id, name, version, or status trigger
  // a re-render. Per rule: code-comments-required (ai.knowledge/global).
  const agents = useAgents(
    (µ) => µ.filter((a) => !a.embedded).map((a) => pick(a, [...placeholderAgentPick])),
    (a, b) => fieldKeyedAgentsEqual(a, b, placeholderAgentPick)
  );
  const agentIds = agents.map((agent) => agent.id);
  const allAgentsOffline = agents.every((agent) => agent.status === 'offline');

  const proxies = agentIds
    .map((id) => broker.acquire(id))
    .filter((µ) => µ !== undefined) as Proxy[];

  return (
    <Scaffold.Placeholder
      header={
        <Scaffold.Header
          center={<Production />}
          right={
            <>
              <div className='self-stretch border-r-1 border-r-base-50' />
              <div>
                <LocalModeSwitch disabled />
              </div>
              <div>
                <Bridge />
              </div>
              <Monitor.Root disabled={agents.length == 0} className='ml-auto'>
                {!!roomId && <Monitor.Panel agentIds={agentIds} roomId={roomId} />}
              </Monitor.Root>
            </>
          }
        >
          <div>
            <SessionsAvatars agents={proxies} />
          </div>
          <div>
            <InviteButton roomId={roomId} />
          </div>
        </Scaffold.Header>
      }
      {...props}
    >
      <div className='grid h-full place-items-center'>
        {/* If there are no agents, show the Linker component */}
        {agents.length === 0 && <Linker className='max-w-[256px]' />}
        {/* UI for single offline bridge */}
        {agents.length === 1 && (
          <SingleBridgeOffline agentVersion={agents[0]?.version} agentName={agents[0]?.name} />
        )}
        {/* UI for multiple offline bridges */}
        {agents.length > 1 && allAgentsOffline && <AllBridgesOffline />}
      </div>
    </Scaffold.Placeholder>
  );
};
